import { useState, useEffect, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import CompanySitesService from './CompanySites.service';
import './CompanySites.css';

/** Site name / address limits (per API: siteAddress1/2, landmark, city, pincode) */
const SITE_NAME_MAX = 100;
const ADDR_LINE1_MAX = 100;
const ADDR_LINE2_MAX = 100;
const LANDMARK_MAX = 50;
const CITY_MAX = 30;
const PINCODE_MAX = 20;

const EMPTY_SITE_ADDRESS_FIELDS = {
  addressLine1: '',
  addressLine2: '',
  landmark: '',
  city: '',
  pincode: '',
};

function getErrorMessage(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (typeof error?.message === 'string') return error.message;
  return '';
}

function looksLikeDuplicateError(message) {
  const m = (message || '').toLowerCase();
  return (
    m.includes('already exist') ||
    m.includes('already exists') ||
    m.includes('duplicate') ||
    m.includes('conflict') ||
    // some APIs use generic "exists"
    m.includes(' exists')
  );
}

function buildCompanyExistsMessage(companyName) {
  const name = (companyName || '').trim();
  return name
    ? `A company named "${name}" already exists.`
    : 'This company already exists.';
}

function buildSiteExistsMessage(siteName) {
  const name = (siteName || '').trim();
  return name
    ? `A site named "${name}" already exists for this company. Please use a different site name.`
    : 'This site already exists for this company. Please use a different site name.';
}

function buildSubCompanyExistsMessage(companyName) {
  const name = (companyName || '').trim();
  return name
    ? `A contractor named "${name}" already exists.`
    : 'This contractor already exists.';
}

/** Map a site record's discrete API fields (siteAddress1/2, landmark, city, pincode) to form fields */
function parseSiteAddressFromStored(site) {
  if (!site || typeof site !== 'object') return { ...EMPTY_SITE_ADDRESS_FIELDS };
  return {
    addressLine1: site.siteAddress1 || '',
    addressLine2: site.siteAddress2 || '',
    landmark: site.landmark || '',
    city: site.city || '',
    pincode: site.pincode || '',
  };
}

/** Readable single line for tables/cards; backend already returns a formatted `siteAddress` string. */
function formatSiteAddressForDisplay(stored) {
  if (!stored || typeof stored !== 'string') return '';
  return stored.trim();
}

function clipSiteField(name, value) {
  if (value == null) return '';
  const v = typeof value === 'string' ? value : String(value);
  switch (name) {
    case 'siteName':
      return v.slice(0, SITE_NAME_MAX);
    case 'addressLine1':
      return v.slice(0, ADDR_LINE1_MAX);
    case 'addressLine2':
      return v.slice(0, ADDR_LINE2_MAX);
    case 'landmark':
      return v.slice(0, LANDMARK_MAX);
    case 'city':
      return v.slice(0, CITY_MAX);
    case 'pincode':
      return v.slice(0, PINCODE_MAX);
    default:
      return v;
  }
}

function CompanySites() {
  const { userData, onLogout } = useOutletContext();
  const companiesSectionRef = useRef(null);
  const [lastLoginTime, setLastLoginTime] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [states, setStates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [addCompanyError, setAddCompanyError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStateId, setFilterStateId] = useState('');
  
  // Add Company Form States
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Delete Confirmation States
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    stateId: '',
    companyName: '',
    companyMailId: '',
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (!showAddForm) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setShowAddForm(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showAddForm]);

  // Sites View States
  const [showSitesView, setShowSitesView] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [sites, setSites] = useState([]);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [sitesError, setSitesError] = useState('');
  const [siteSearchQuery, setSiteSearchQuery] = useState('');
  const [showAddSiteForm, setShowAddSiteForm] = useState(false);
  const [isSubmittingSite, setIsSubmittingSite] = useState(false);
  const [siteFormData, setSiteFormData] = useState({
    siteName: '',
    ...EMPTY_SITE_ADDRESS_FIELDS,
  });
  const [siteFormErrors, setSiteFormErrors] = useState({});
  
  // Delete Site Confirmation States
  const [showDeleteSiteConfirm, setShowDeleteSiteConfirm] = useState(false);
  const [siteToDelete, setSiteToDelete] = useState(null);
  const [isDeletingSite, setIsDeletingSite] = useState(false);
  
  // Edit Company States
  const [showEditCompanyForm, setShowEditCompanyForm] = useState(false);
  const [companyToEdit, setCompanyToEdit] = useState(null);
  const [editCompanyData, setEditCompanyData] = useState({
    stateId: '',
    companyName: '',
    companyMailId: '',
  });
  const [editCompanyErrors, setEditCompanyErrors] = useState({});
  const [isUpdatingCompany, setIsUpdatingCompany] = useState(false);
  
  // Edit Site States
  const [showEditSiteForm, setShowEditSiteForm] = useState(false);
  const [siteToEdit, setSiteToEdit] = useState(null);
  const [editSiteData, setEditSiteData] = useState({
    siteName: '',
    ...EMPTY_SITE_ADDRESS_FIELDS,
  });
  const [editSiteErrors, setEditSiteErrors] = useState({});
  const [isUpdatingSite, setIsUpdatingSite] = useState(false);

  // Contractors (Sub-companies) View States
  const [showContractorsView, setShowContractorsView] = useState(false);
  const [parentCompany, setParentCompany] = useState(null);
  const [sitesViewParent, setSitesViewParent] = useState(null);
  const [subCompanies, setSubCompanies] = useState([]);
  const [isLoadingSubCompanies, setIsLoadingSubCompanies] = useState(false);
  const [contractorsError, setContractorsError] = useState('');
  const [contractorSearchQuery, setContractorSearchQuery] = useState('');
  const [showAddSubCompanyForm, setShowAddSubCompanyForm] = useState(false);
  const [addSubCompanyError, setAddSubCompanyError] = useState('');
  const [isSubmittingSubCompany, setIsSubmittingSubCompany] = useState(false);
  const [subCompanyFormData, setSubCompanyFormData] = useState({
    companyName: '',
    companyMailId: '',
  });
  const [subCompanyFormErrors, setSubCompanyFormErrors] = useState({});
  const [showEditSubCompanyForm, setShowEditSubCompanyForm] = useState(false);
  const [subCompanyToEdit, setSubCompanyToEdit] = useState(null);
  const [editSubCompanyData, setEditSubCompanyData] = useState({
    companyName: '',
    companyMailId: '',
  });
  const [editSubCompanyErrors, setEditSubCompanyErrors] = useState({});
  const [isUpdatingSubCompany, setIsUpdatingSubCompany] = useState(false);
  const [deleteContext, setDeleteContext] = useState('company');

  const { userDetails, dashboardList } = userData || {};
  const navigate = useNavigate();

  useEffect(() => {
    // Get last login time from localStorage
    const stored = localStorage.getItem('lastLoginTime');
    if (stored) {
      setLastLoginTime(new Date(stored).toLocaleString());
    } else {
      const now = new Date();
      localStorage.setItem('lastLoginTime', now.toISOString());
      setLastLoginTime(now.toLocaleString());
    }

    // Load companies and state list on mount
    loadCompanies();
    loadStateList();
  }, []);

  const loadStateList = async () => {
    try {
      const data = await CompanySitesService.getStateList();
      setStates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading state list:', err);
      setStates([]);
    }
  };

  const loadCompanies = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await CompanySitesService.getCompanies();
      setCompanies(data);
    } catch (error) {
      console.error('Error loading companies:', error);
      setError('Failed to load companies. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.stateId && formData.stateId !== 0) {
      errors.stateId = 'State is required';
    }

    if (!formData.companyName.trim()) {
      errors.companyName = 'Company name is required';
    } else if (formData.companyName.trim().length < 2) {
      errors.companyName = 'Company name must be at least 2 characters';
    } else if (formData.companyName.trim().length > 500) {
      errors.companyName = 'Company name must not exceed 500 characters';
    }

    if (formData.companyMailId.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.companyMailId.trim())) {
        errors.companyMailId = 'Please enter a valid email address';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Enforce character limits
    let limitedValue = value;
    if (name === 'companyName' && value.length > 500) {
      limitedValue = value.substring(0, 500);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: limitedValue,
    }));
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setAddCompanyError('');

    try {
      const payload = {
        stateId: Number(formData.stateId),
        companyName: formData.companyName.trim(),
        ...(formData.companyMailId.trim() && { companyMailId: formData.companyMailId.trim() }),
      };

      await CompanySitesService.createCompany(payload);
      
      setFormData({
        stateId: '',
        companyName: '',
        companyMailId: '',
      });
      setFormErrors({});
      setShowAddForm(false);
      setAddCompanyError('');
      
      // Reload companies list
      await loadCompanies();
    } catch (error) {
      console.error('Error creating company:', error);
      const msg = getErrorMessage(error);
      // Prefer backend message when available
      setAddCompanyError(msg || 'Unable to create the company right now. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      stateId: '',
      companyName: '',
      companyMailId: '',
    });
    setFormErrors({});
    setAddCompanyError('');
    setShowAddForm(false);
    setShowEditCompanyForm(false);
    setCompanyToEdit(null);
  };

  const handleViewSites = async (companyId, options = {}) => {
    const { fromContractors = false, parent = null } = options;

    let company;
    if (fromContractors) {
      company = subCompanies.find((c) => (c.companyId || c.id) === companyId);
    } else {
      company = companies.find((c) => (c.companyId || c.id) === companyId);
    }
    if (!company) return;

    setSelectedCompany(company);
    setSitesViewParent(fromContractors ? parent : null);
    if (!fromContractors) {
      setParentCompany(null);
    }
    setShowContractorsView(false);
    setShowSitesView(true);
    setSitesError('');
    await loadSites(companyId);
  };

  const loadSubCompanies = async (companyId) => {
    setIsLoadingSubCompanies(true);
    setContractorsError('');
    try {
      const data = await CompanySitesService.getSubCompanies(companyId);
      setSubCompanies(data.subCompanies || []);
    } catch (error) {
      console.error('Error loading contractors:', error);
      setContractorsError('Failed to load contractors. Please try again.');
      setSubCompanies([]);
    } finally {
      setIsLoadingSubCompanies(false);
    }
  };

  const handleViewContractors = async (companyId) => {
    const company = companies.find((c) => (c.companyId || c.id) === companyId);
    if (!company) return;

    setParentCompany(company);
    setShowContractorsView(true);
    setContractorsError('');
    setContractorSearchQuery('');
    setShowEditCompanyForm(false);
    setCompanyToEdit(null);
    setShowAddSubCompanyForm(false);
    setShowEditSubCompanyForm(false);
    setSubCompanyToEdit(null);
    await loadSubCompanies(companyId);
  };

  const handleCloseContractorsView = () => {
    setShowContractorsView(false);
    setParentCompany(null);
    setSubCompanies([]);
    setContractorsError('');
    setContractorSearchQuery('');
    setShowAddSubCompanyForm(false);
    setAddSubCompanyError('');
    setSubCompanyFormData({ companyName: '', companyMailId: '' });
    setSubCompanyFormErrors({});
    setShowEditSubCompanyForm(false);
    setSubCompanyToEdit(null);
    setEditSubCompanyData({ companyName: '', companyMailId: '' });
    setEditSubCompanyErrors({});
  };

  const loadSites = async (companyId) => {
    setIsLoadingSites(true);
    setSitesError('');
    try {
      const data = await CompanySitesService.getCompanySites(companyId);
      setSites(data.sites || []);
    } catch (error) {
      console.error('Error loading sites:', error);
      setSitesError('Failed to load sites. Please try again.');
      setSites([]);
    } finally {
      setIsLoadingSites(false);
    }
  };

  const handleCloseSitesView = () => {
    const parent = sitesViewParent;

    setShowSitesView(false);
    setSelectedCompany(null);
    setSites([]);
    setShowAddSiteForm(false);
    setSiteFormData({ siteName: '', ...EMPTY_SITE_ADDRESS_FIELDS });
    setSiteFormErrors({});
    setSitesError('');
    setSiteSearchQuery('');
    setShowDeleteSiteConfirm(false);
    setSiteToDelete(null);
    setShowEditSiteForm(false);
    setSiteToEdit(null);

    if (parent) {
      setParentCompany(parent);
      setShowContractorsView(true);
      setSitesViewParent(null);
      loadSubCompanies(parent.companyId || parent.id);
    } else {
      setSitesViewParent(null);
    }
  };

  const validateSiteAddressParts = (addr, errors) => {
    if (!addr.addressLine1?.trim()) {
      errors.addressLine1 = 'Address line 1 is required';
    } else if (addr.addressLine1.trim().length > ADDR_LINE1_MAX) {
      errors.addressLine1 = `Address line 1 must not exceed ${ADDR_LINE1_MAX} characters`;
    }

    if (addr.addressLine2 && addr.addressLine2.length > ADDR_LINE2_MAX) {
      errors.addressLine2 = `Address line 2 must not exceed ${ADDR_LINE2_MAX} characters`;
    }

    if (!addr.landmark?.trim()) {
      errors.landmark = 'Landmark is required';
    } else if (addr.landmark.trim().length > LANDMARK_MAX) {
      errors.landmark = `Landmark must not exceed ${LANDMARK_MAX} characters`;
    }

    if (!addr.city?.trim()) {
      errors.city = 'City is required';
    } else if (addr.city.trim().length > CITY_MAX) {
      errors.city = `City must not exceed ${CITY_MAX} characters`;
    }

    const pinDigits = (addr.pincode || '').trim();
    if (!pinDigits) {
      errors.pincode = 'Pincode is required';
    } else if (pinDigits.length > PINCODE_MAX) {
      errors.pincode = `Pincode must not exceed ${PINCODE_MAX} characters`;
    }
  };

  const validateSiteForm = () => {
    const errors = {};

    if (!siteFormData.siteName.trim()) {
      errors.siteName = 'Site name is required';
    } else if (siteFormData.siteName.trim().length < 2) {
      errors.siteName = 'Site name must be at least 2 characters';
    } else if (siteFormData.siteName.trim().length > SITE_NAME_MAX) {
      errors.siteName = `Site name must not exceed ${SITE_NAME_MAX} characters`;
    }

    validateSiteAddressParts(siteFormData, errors);

    setSiteFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSiteInputChange = (e) => {
    const { name, value } = e.target;
    const limitedValue = clipSiteField(name, value);

    setSiteFormData((prev) => ({
      ...prev,
      [name]: limitedValue,
    }));
    if (siteFormErrors[name]) {
      setSiteFormErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    if (name !== 'siteName' && siteFormErrors.addressMerged) {
      setSiteFormErrors((prev) => ({ ...prev, addressMerged: '' }));
    }
  };

  const handleSiteSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateSiteForm()) {
      return;
    }

    setIsSubmittingSite(true);
    setSitesError('');

    try {
      const payload = {
        companyId: selectedCompany?.companyId || selectedCompany?.id,
        siteName: siteFormData.siteName.trim(),
        siteAddress1: siteFormData.addressLine1.trim(),
        siteAddress2: siteFormData.addressLine2.trim(),
        landmark: siteFormData.landmark.trim(),
        city: siteFormData.city.trim(),
        pincode: siteFormData.pincode.trim(),
      };

      await CompanySitesService.createSite(payload);
      
      setSiteFormData({ siteName: '', ...EMPTY_SITE_ADDRESS_FIELDS });
      setSiteFormErrors({});
      setShowAddSiteForm(false);
      
      // Reload sites list
      await loadSites(selectedCompany?.companyId || selectedCompany?.id);
    } catch (error) {
      console.error('Error creating site:', error);
      const msg = getErrorMessage(error);
      // Prefer backend message when available
      setSitesError(msg || 'Unable to create the site right now. Please try again.');
    } finally {
      setIsSubmittingSite(false);
    }
  };

  const handleSiteCancel = () => {
    setSiteFormData({ siteName: '', ...EMPTY_SITE_ADDRESS_FIELDS });
    setSiteFormErrors({});
    setShowAddSiteForm(false);
    setShowEditSiteForm(false);
    setSiteToEdit(null);
  };

  // Filter sites based on search query
  const filteredSites = sites.filter(site => {
    if (!siteSearchQuery.trim()) return true;
    const query = siteSearchQuery.toLowerCase();
    const siteName = (site.siteName || '').toLowerCase();
    const siteAddress = (site.siteAddress || '').toLowerCase();
    return siteName.includes(query) || siteAddress.includes(query);
  });

  const handleDeleteSiteClick = (site) => {
    setSiteToDelete(site);
    setShowDeleteSiteConfirm(true);
  };

  const handleDeleteSiteConfirm = async () => {
    if (!siteToDelete) return;

    setIsDeletingSite(true);
    setSitesError('');

    try {
      const siteId = siteToDelete.siteId || siteToDelete.id;
      await CompanySitesService.deleteSite(siteId);
      
      // Reload sites list
      await loadSites(selectedCompany?.companyId || selectedCompany?.id);
      setShowDeleteSiteConfirm(false);
      setSiteToDelete(null);
    } catch (error) {
      console.error('Error deleting site:', error);
      setSitesError(error.message || 'Failed to delete site. Please try again.');
      setShowDeleteSiteConfirm(false);
      setSiteToDelete(null);
    } finally {
      setIsDeletingSite(false);
    }
  };

  const handleDeleteSiteCancel = () => {
    setShowDeleteSiteConfirm(false);
    setSiteToDelete(null);
  };

  const handleDeleteClick = (company) => {
    setCompanyToDelete(company);
    setDeleteContext('company');
    setShowDeleteConfirm(true);
  };

  const handleDeleteSubCompanyClick = (subCompany) => {
    setCompanyToDelete(subCompany);
    setDeleteContext('subCompany');
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!companyToDelete) return;

    setIsDeleting(true);
    setError('');
    setContractorsError('');

    try {
      const companyId = companyToDelete.companyId || companyToDelete.id;
      await CompanySitesService.deleteCompany(companyId);

      if (deleteContext === 'subCompany' && parentCompany) {
        await loadSubCompanies(parentCompany.companyId || parentCompany.id);
      } else {
        await loadCompanies();
      }
      setShowDeleteConfirm(false);
      setCompanyToDelete(null);
      setDeleteContext('company');
    } catch (error) {
      console.error('Error deleting company:', error);
      const msg = error.message || 'Failed to delete. Please try again.';
      if (deleteContext === 'subCompany') {
        setContractorsError(msg);
      } else {
        setError(msg);
      }
      setShowDeleteConfirm(false);
      setCompanyToDelete(null);
      setDeleteContext('company');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setCompanyToDelete(null);
    setDeleteContext('company');
  };

  // Edit Company Functions
  const handleEditCompanyClick = (company) => {
    setCompanyToEdit(company);
    setEditCompanyData({
      stateId: company.stateId ?? '',
      companyName: company.companyName || company.company_name || company.name || '',
      companyMailId: company.companyMailId || company.company_mail_id || company.companyMail || company.email || '',
    });
    setEditCompanyErrors({});
    setShowEditCompanyForm(true);
    setShowAddForm(false);
    setTimeout(() => companiesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleEditCompanyInputChange = (e) => {
    const { name, value } = e.target;
    
    let limitedValue = value;
    if (name === 'companyName' && value.length > 500) {
      limitedValue = value.substring(0, 500);
    }
    
    setEditCompanyData(prev => ({
      ...prev,
      [name]: limitedValue,
    }));
    if (editCompanyErrors[name]) {
      setEditCompanyErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateEditCompanyForm = () => {
    const errors = {};

    if (!editCompanyData.stateId && editCompanyData.stateId !== 0) {
      errors.stateId = 'State is required';
    }

    if (!editCompanyData.companyName.trim()) {
      errors.companyName = 'Company name is required';
    } else if (editCompanyData.companyName.trim().length < 2) {
      errors.companyName = 'Company name must be at least 2 characters';
    } else if (editCompanyData.companyName.trim().length > 500) {
      errors.companyName = 'Company name must not exceed 500 characters';
    }

    if (editCompanyData.companyMailId.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(editCompanyData.companyMailId.trim())) {
        errors.companyMailId = 'Please enter a valid email address';
      }
    }

    setEditCompanyErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    
    if (!validateEditCompanyForm()) {
      return;
    }

    setIsUpdatingCompany(true);
    setError('');

    try {
      const payload = {
        companyId: companyToEdit?.companyId || companyToEdit?.id,
        stateId: Number(editCompanyData.stateId),
        companyName: editCompanyData.companyName.trim(),
        ...(editCompanyData.companyMailId != null && editCompanyData.companyMailId.trim() !== '' && { companyMailId: editCompanyData.companyMailId.trim() }),
      };

      await CompanySitesService.updateCompany(payload);
      
      setShowEditCompanyForm(false);
      setCompanyToEdit(null);
      setEditCompanyData({ stateId: '', companyName: '', companyMailId: '' });
      setEditCompanyErrors({});
      
      // Reload companies list
      await loadCompanies();
    } catch (error) {
      console.error('Error updating company:', error);
      setError(error.message || 'Failed to update company. Please try again.');
    } finally {
      setIsUpdatingCompany(false);
    }
  };

  const handleEditCompanyCancel = () => {
    setShowEditCompanyForm(false);
    setCompanyToEdit(null);
    setEditCompanyData({ stateId: '', companyName: '', companyMailId: '' });
    setEditCompanyErrors({});
  };

  // Edit Site Functions
  const handleEditSiteClick = (site) => {
    setSiteToEdit(site);
    const parsed = parseSiteAddressFromStored(site);
    setEditSiteData({
      siteName: site.siteName || '',
      ...parsed,
    });
    setEditSiteErrors({});
    setShowEditSiteForm(true);
    setShowAddSiteForm(false); // Hide add form when editing
  };

  const handleEditSiteInputChange = (e) => {
    const { name, value } = e.target;
    const limitedValue = clipSiteField(name, value);

    setEditSiteData((prev) => ({
      ...prev,
      [name]: limitedValue,
    }));
    if (editSiteErrors[name]) {
      setEditSiteErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    if (name !== 'siteName' && editSiteErrors.addressMerged) {
      setEditSiteErrors((prev) => ({ ...prev, addressMerged: '' }));
    }
  };

  const validateEditSiteForm = () => {
    const errors = {};

    if (!editSiteData.siteName.trim()) {
      errors.siteName = 'Site name is required';
    } else if (editSiteData.siteName.trim().length < 2) {
      errors.siteName = 'Site name must be at least 2 characters';
    } else if (editSiteData.siteName.trim().length > SITE_NAME_MAX) {
      errors.siteName = `Site name must not exceed ${SITE_NAME_MAX} characters`;
    }

    validateSiteAddressParts(editSiteData, errors);

    setEditSiteErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateSite = async (e) => {
    e.preventDefault();
    
    if (!validateEditSiteForm()) {
      return;
    }

    setIsUpdatingSite(true);
    setSitesError('');

    try {
      const payload = {
        siteId: siteToEdit?.siteId || siteToEdit?.id,
        siteName: editSiteData.siteName.trim(),
        siteAddress1: editSiteData.addressLine1.trim(),
        siteAddress2: editSiteData.addressLine2.trim(),
        landmark: editSiteData.landmark.trim(),
        city: editSiteData.city.trim(),
        pincode: editSiteData.pincode.trim(),
      };

      await CompanySitesService.updateSite(payload);
      
      setShowEditSiteForm(false);
      setSiteToEdit(null);
      setEditSiteData({ siteName: '', ...EMPTY_SITE_ADDRESS_FIELDS });
      setEditSiteErrors({});
      
      // Reload sites list
      await loadSites(selectedCompany?.companyId || selectedCompany?.id);
    } catch (error) {
      console.error('Error updating site:', error);
      setSitesError(error.message || 'Failed to update site. Please try again.');
    } finally {
      setIsUpdatingSite(false);
    }
  };

  const handleEditSiteCancel = () => {
    setShowEditSiteForm(false);
    setSiteToEdit(null);
    setEditSiteData({ siteName: '', ...EMPTY_SITE_ADDRESS_FIELDS });
    setEditSiteErrors({});
  };

  const validateSubCompanyForm = (data) => {
    const errors = {};

    if (!data.companyName.trim()) {
      errors.companyName = 'Contractor name is required';
    } else if (data.companyName.trim().length < 2) {
      errors.companyName = 'Contractor name must be at least 2 characters';
    } else if (data.companyName.trim().length > 500) {
      errors.companyName = 'Contractor name must not exceed 500 characters';
    }

    if (data.companyMailId.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.companyMailId.trim())) {
        errors.companyMailId = 'Please enter a valid email address';
      }
    }

    return errors;
  };

  const handleSubCompanyInputChange = (e) => {
    const { name, value } = e.target;
    let limitedValue = value;
    if (name === 'companyName' && value.length > 500) {
      limitedValue = value.substring(0, 500);
    }

    setSubCompanyFormData((prev) => ({
      ...prev,
      [name]: limitedValue,
    }));
    if (subCompanyFormErrors[name]) {
      setSubCompanyFormErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubCompanySubmit = async (e) => {
    e.preventDefault();

    const errors = validateSubCompanyForm(subCompanyFormData);
    setSubCompanyFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSubmittingSubCompany(true);
    setAddSubCompanyError('');

    try {
      const payload = {
        companyId: parentCompany?.companyId || parentCompany?.id,
        companyName: subCompanyFormData.companyName.trim(),
        ...(subCompanyFormData.companyMailId.trim() && {
          companyMailId: subCompanyFormData.companyMailId.trim(),
        }),
      };

      await CompanySitesService.createSubCompany(payload);

      setSubCompanyFormData({ companyName: '', companyMailId: '' });
      setSubCompanyFormErrors({});
      setShowAddSubCompanyForm(false);
      setAddSubCompanyError('');

      await loadSubCompanies(parentCompany?.companyId || parentCompany?.id);
    } catch (error) {
      console.error('Error creating contractor:', error);
      const msg = getErrorMessage(error);
      setAddSubCompanyError(msg || 'Unable to create the contractor right now. Please try again.');
    } finally {
      setIsSubmittingSubCompany(false);
    }
  };

  const handleSubCompanyCancel = () => {
    setSubCompanyFormData({ companyName: '', companyMailId: '' });
    setSubCompanyFormErrors({});
    setAddSubCompanyError('');
    setShowAddSubCompanyForm(false);
  };

  const handleEditSubCompanyClick = (subCompany) => {
    setSubCompanyToEdit(subCompany);
    setEditSubCompanyData({
      companyName: subCompany.companyName || '',
      companyMailId: subCompany.companyMailId || '',
    });
    setEditSubCompanyErrors({});
    setShowEditSubCompanyForm(true);
    setShowAddSubCompanyForm(false);
  };

  const handleEditSubCompanyInputChange = (e) => {
    const { name, value } = e.target;
    let limitedValue = value;
    if (name === 'companyName' && value.length > 500) {
      limitedValue = value.substring(0, 500);
    }

    setEditSubCompanyData((prev) => ({
      ...prev,
      [name]: limitedValue,
    }));
    if (editSubCompanyErrors[name]) {
      setEditSubCompanyErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleUpdateSubCompany = async (e) => {
    e.preventDefault();

    const errors = validateSubCompanyForm(editSubCompanyData);
    setEditSubCompanyErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsUpdatingSubCompany(true);
    setContractorsError('');

    try {
      const payload = {
        companyId: subCompanyToEdit?.companyId || subCompanyToEdit?.id,
        companyName: editSubCompanyData.companyName.trim(),
        ...(editSubCompanyData.companyMailId != null &&
          editSubCompanyData.companyMailId.trim() !== '' && {
            companyMailId: editSubCompanyData.companyMailId.trim(),
          }),
      };

      await CompanySitesService.updateSubCompany(payload);

      setShowEditSubCompanyForm(false);
      setSubCompanyToEdit(null);
      setEditSubCompanyData({ companyName: '', companyMailId: '' });
      setEditSubCompanyErrors({});

      await loadSubCompanies(parentCompany?.companyId || parentCompany?.id);
    } catch (error) {
      console.error('Error updating contractor:', error);
      setContractorsError(error.message || 'Failed to update contractor. Please try again.');
    } finally {
      setIsUpdatingSubCompany(false);
    }
  };

  const handleEditSubCompanyCancel = () => {
    setShowEditSubCompanyForm(false);
    setSubCompanyToEdit(null);
    setEditSubCompanyData({ companyName: '', companyMailId: '' });
    setEditSubCompanyErrors({});
  };

  const getRouteForItem = (item) => {
    const routeMap = {
      'Role Access Management': '/admin/role-access-management',
      'QR Code Generator': '/admin/qr-code-generator',
      'Company Sites': '/admin/company-sites',
    };
    return routeMap[item] || null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  // Filter companies based on search query and state filter
  const filteredCompanies = companies.filter(company => {
    if (filterStateId && String(company.stateId) !== String(filterStateId)) {
      return false;
    }
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const companyName = (company.companyName || '').toLowerCase();
    const companyMailId = (company.companyMailId || '').toLowerCase();
    return companyName.includes(query) || companyMailId.includes(query);
  });

  const filteredSubCompanies = subCompanies.filter((subCompany) => {
    if (!contractorSearchQuery.trim()) return true;
    const query = contractorSearchQuery.toLowerCase();
    const companyName = (subCompany.companyName || '').toLowerCase();
    const companyMailId = (subCompany.companyMailId || '').toLowerCase();
    return companyName.includes(query) || companyMailId.includes(query);
  });

  return (
    <div className="company-sites-page">
      <div className="company-sites-content">
        <div className="main-content-wrapper">
          {showSitesView && selectedCompany ? (
            /* Full-screen Sites View – companies list hidden */
            <div className="sites-view-full">
              <div className="sites-view-header">
                <h2 className="sites-view-title">{selectedCompany.companyName}</h2>
                <button type="button" className="sites-view-back" onClick={handleCloseSitesView}>
                  ← {sitesViewParent ? 'Back to Contractors' : 'Back to Companies'}
                </button>
              </div>
              {sitesError && (
                <div className="error-message" role="alert" aria-live="polite">
                  <span className="message-icon" aria-hidden="true">!</span>
                  <span className="error-message-text">{sitesError}</span>
                  <button
                    type="button"
                    className="error-message-close"
                    onClick={() => setSitesError('')}
                    aria-label="Close message"
                    title="Close"
                  >
                    ×
                  </button>
                </div>
              )}
              <div className="sites-section-header">
                <h3>Sites ({filteredSites.length})</h3>
                <div className="sites-header-actions">
                  <div className="search-container">
                    <input
                      type="text"
                      placeholder="Search site name..."
                      value={siteSearchQuery}
                      onChange={(e) => setSiteSearchQuery(e.target.value)}
                      className="search-input"
                    />
                    <span className="search-icon">🔍</span>
                  </div>
                  <button className="refresh-button" onClick={() => loadSites(selectedCompany.companyId || selectedCompany.id)} disabled={isLoadingSites} title="Refresh list">↻ Refresh</button>
                  <button className="add-site-button" onClick={() => { setShowAddSiteForm(!showAddSiteForm); setShowEditSiteForm(false); setSiteToEdit(null); }}>
                    {showAddSiteForm ? 'Cancel' : '+ Add Site'}
                  </button>
                </div>
              </div>
              {showEditSiteForm && siteToEdit && (
                <div className="add-site-form-container">
                  <div className="form-card">
                    <div className="form-card-header"><div className="form-icon">✏️</div><h2>Edit Site</h2><p className="form-subtitle">Update site details</p></div>
                    <form onSubmit={handleUpdateSite} className="company-form">
                      <div className="form-group">
                        <label htmlFor="editSiteName">Site Name <span className="required">*</span> <span className="character-count">({editSiteData.siteName.length}/{SITE_NAME_MAX})</span></label>
                        <input type="text" id="editSiteName" name="siteName" value={editSiteData.siteName} onChange={handleEditSiteInputChange} placeholder="Enter site name" className={editSiteErrors.siteName ? 'error' : ''} disabled={isUpdatingSite} maxLength={SITE_NAME_MAX} />
                        {editSiteErrors.siteName && <span className="field-error">{editSiteErrors.siteName}</span>}
                      </div>
                      <div className="site-address-section">
                        <p className="site-address-section-title">Site address</p>
                        {editSiteErrors.addressMerged && <span className="field-error site-address-merge-error">{editSiteErrors.addressMerged}</span>}
                        <div className="form-group">
                          <label htmlFor="editAddressLine1">Address line 1 <span className="required">*</span> <span className="character-count">({editSiteData.addressLine1.length}/{ADDR_LINE1_MAX})</span></label>
                          <input type="text" id="editAddressLine1" name="addressLine1" value={editSiteData.addressLine1} onChange={handleEditSiteInputChange} placeholder="Street, building, etc." className={editSiteErrors.addressLine1 ? 'error' : ''} disabled={isUpdatingSite} maxLength={ADDR_LINE1_MAX} />
                          {editSiteErrors.addressLine1 && <span className="field-error">{editSiteErrors.addressLine1}</span>}
                        </div>
                        <div className="form-group">
                          <label htmlFor="editAddressLine2">Address line 2 <span className="character-count">({editSiteData.addressLine2.length}/{ADDR_LINE2_MAX})</span></label>
                          <input type="text" id="editAddressLine2" name="addressLine2" value={editSiteData.addressLine2} onChange={handleEditSiteInputChange} placeholder="Optional" className={editSiteErrors.addressLine2 ? 'error' : ''} disabled={isUpdatingSite} maxLength={ADDR_LINE2_MAX} />
                          {editSiteErrors.addressLine2 && <span className="field-error">{editSiteErrors.addressLine2}</span>}
                        </div>
                        <div className="form-group">
                          <label htmlFor="editLandmark">Landmark <span className="required">*</span> <span className="character-count">({editSiteData.landmark.length}/{LANDMARK_MAX})</span></label>
                          <input type="text" id="editLandmark" name="landmark" value={editSiteData.landmark} onChange={handleEditSiteInputChange} placeholder="Nearby landmark" className={editSiteErrors.landmark ? 'error' : ''} disabled={isUpdatingSite} maxLength={LANDMARK_MAX} />
                          {editSiteErrors.landmark && <span className="field-error">{editSiteErrors.landmark}</span>}
                        </div>
                        <div className="form-group form-group-inline">
                          <div className="form-group">
                            <label htmlFor="editCity">City <span className="required">*</span> <span className="character-count">({editSiteData.city.length}/{CITY_MAX})</span></label>
                            <input type="text" id="editCity" name="city" value={editSiteData.city} onChange={handleEditSiteInputChange} placeholder="City" className={editSiteErrors.city ? 'error' : ''} disabled={isUpdatingSite} maxLength={CITY_MAX} />
                            {editSiteErrors.city && <span className="field-error">{editSiteErrors.city}</span>}
                          </div>
                          <div className="form-group">
                            <label htmlFor="editPincode">Pincode <span className="required">*</span> <span className="character-count">({editSiteData.pincode.length}/{PINCODE_MAX})</span></label>
                            <input type="text" id="editPincode" name="pincode" inputMode="numeric" autoComplete="postal-code" value={editSiteData.pincode} onChange={handleEditSiteInputChange} placeholder="Pincode" className={editSiteErrors.pincode ? 'error' : ''} disabled={isUpdatingSite} maxLength={PINCODE_MAX} />
                            {editSiteErrors.pincode && <span className="field-error">{editSiteErrors.pincode}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="form-actions">
                        <button type="button" className="cancel-button" onClick={handleEditSiteCancel} disabled={isUpdatingSite}>Cancel</button>
                        <button type="submit" className="submit-button" disabled={isUpdatingSite}>{isUpdatingSite ? <><span className="button-spinner"></span>Updating...</> : <><span>✓</span>Update Site</>}</button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
              {showAddSiteForm && (
                <div className="add-site-form-container">
                  <div className="form-card">
                    <div className="form-card-header"><div className="form-icon">📍</div><h2>Add New Site</h2><p className="form-subtitle">Fill in the details to create a new site</p></div>
                    <form onSubmit={handleSiteSubmit} className="company-form">
                      <div className="form-group">
                        <label htmlFor="siteName">Site Name <span className="required">*</span> <span className="character-count">({siteFormData.siteName.length}/{SITE_NAME_MAX})</span></label>
                        <input type="text" id="siteName" name="siteName" value={siteFormData.siteName} onChange={handleSiteInputChange} placeholder="Enter site name" className={siteFormErrors.siteName ? 'error' : ''} disabled={isSubmittingSite} maxLength={SITE_NAME_MAX} />
                        {siteFormErrors.siteName && <span className="field-error">{siteFormErrors.siteName}</span>}
                      </div>
                      <div className="site-address-section">
                        <p className="site-address-section-title">Site address</p>
                        {siteFormErrors.addressMerged && <span className="field-error site-address-merge-error">{siteFormErrors.addressMerged}</span>}
                        <div className="form-group">
                          <label htmlFor="addressLine1">Address line 1 <span className="required">*</span> <span className="character-count">({siteFormData.addressLine1.length}/{ADDR_LINE1_MAX})</span></label>
                          <input type="text" id="addressLine1" name="addressLine1" value={siteFormData.addressLine1} onChange={handleSiteInputChange} placeholder="Street, building, etc." className={siteFormErrors.addressLine1 ? 'error' : ''} disabled={isSubmittingSite} maxLength={ADDR_LINE1_MAX} />
                          {siteFormErrors.addressLine1 && <span className="field-error">{siteFormErrors.addressLine1}</span>}
                        </div>
                        <div className="form-group">
                          <label htmlFor="addressLine2">Address line 2 <span className="character-count">({siteFormData.addressLine2.length}/{ADDR_LINE2_MAX})</span></label>
                          <input type="text" id="addressLine2" name="addressLine2" value={siteFormData.addressLine2} onChange={handleSiteInputChange} placeholder="Optional" className={siteFormErrors.addressLine2 ? 'error' : ''} disabled={isSubmittingSite} maxLength={ADDR_LINE2_MAX} />
                          {siteFormErrors.addressLine2 && <span className="field-error">{siteFormErrors.addressLine2}</span>}
                        </div>
                        <div className="form-group">
                          <label htmlFor="landmark">Landmark <span className="required">*</span> <span className="character-count">({siteFormData.landmark.length}/{LANDMARK_MAX})</span></label>
                          <input type="text" id="landmark" name="landmark" value={siteFormData.landmark} onChange={handleSiteInputChange} placeholder="Nearby landmark" className={siteFormErrors.landmark ? 'error' : ''} disabled={isSubmittingSite} maxLength={LANDMARK_MAX} />
                          {siteFormErrors.landmark && <span className="field-error">{siteFormErrors.landmark}</span>}
                        </div>
                        <div className="form-group form-group-inline">
                          <div className="form-group">
                            <label htmlFor="city">City <span className="required">*</span> <span className="character-count">({siteFormData.city.length}/{CITY_MAX})</span></label>
                            <input type="text" id="city" name="city" value={siteFormData.city} onChange={handleSiteInputChange} placeholder="City" className={siteFormErrors.city ? 'error' : ''} disabled={isSubmittingSite} maxLength={CITY_MAX} />
                            {siteFormErrors.city && <span className="field-error">{siteFormErrors.city}</span>}
                          </div>
                          <div className="form-group">
                            <label htmlFor="pincode">Pincode <span className="required">*</span> <span className="character-count">({siteFormData.pincode.length}/{PINCODE_MAX})</span></label>
                            <input type="text" id="pincode" name="pincode" inputMode="numeric" autoComplete="postal-code" value={siteFormData.pincode} onChange={handleSiteInputChange} placeholder="Pincode" className={siteFormErrors.pincode ? 'error' : ''} disabled={isSubmittingSite} maxLength={PINCODE_MAX} />
                            {siteFormErrors.pincode && <span className="field-error">{siteFormErrors.pincode}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="form-actions">
                        <button type="button" className="cancel-button" onClick={handleSiteCancel} disabled={isSubmittingSite}>Cancel</button>
                        <button type="submit" className="submit-button" disabled={isSubmittingSite}>{isSubmittingSite ? <><span className="button-spinner"></span>Creating...</> : <><span>✓</span>Create Site</>}</button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
              {isLoadingSites ? (
                <div className="loading-state"><div className="loading-spinner"></div><p>Loading sites...</p></div>
              ) : filteredSites.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">📍</div>
                  <h3>{siteSearchQuery ? 'No Sites Found' : 'No Sites Found'}</h3>
                  <p>{siteSearchQuery ? `No sites match "${siteSearchQuery}".` : 'Add your first site for this company.'}</p>
                  {!showAddSiteForm && !siteSearchQuery && <button className="empty-state-button" onClick={() => setShowAddSiteForm(true)}><span>+</span> Add Your First Site</button>}
                </div>
              ) : (
                <div className="sites-list">
                  {filteredSites.map((site) => (
                    <div key={site.siteId || site.id} className="site-card">
                      <div className="site-card-accent"></div>
                      <div className="site-card-content">
                        <div className="site-card-top">
                          <div className="site-icon-wrapper">
                            <svg className="site-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                          </div>
                          <div className="site-title-block">
                            <h3 className="site-name">{site.siteName || 'Unnamed Site'}</h3>
                          </div>
                        </div>
                        <div className="site-card-body">
                          <div className="site-details-list">
                            <div className="site-detail">
                              <svg className="detail-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                              <span className="detail-value">{formatSiteAddressForDisplay(site.siteAddress) || 'N/A'}</span>
                            </div>
                            {site.startDate && (
                              <div className="site-detail">
                                <svg className="detail-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                <span className="detail-value">{formatDate(site.startDate)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="site-card-actions">
                          <button type="button" className="site-card-btn edit-site-button" onClick={() => handleEditSiteClick(site)}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            Edit
                          </button>
                          <button type="button" className="site-card-btn delete-site-button" onClick={() => handleDeleteSiteClick(site)}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : showContractorsView && parentCompany ? (
            <div className="sites-view-full">
              <div className="sites-view-header">
                <h2 className="sites-view-title">{parentCompany.companyName}</h2>
                <button type="button" className="sites-view-back" onClick={handleCloseContractorsView}>
                  ← Back to Companies
                </button>
              </div>
              {contractorsError && (
                <div className="error-message" role="alert" aria-live="polite">
                  <span className="message-icon" aria-hidden="true">!</span>
                  <span className="error-message-text">{contractorsError}</span>
                  <button
                    type="button"
                    className="error-message-close"
                    onClick={() => setContractorsError('')}
                    aria-label="Close message"
                    title="Close"
                  >
                    ×
                  </button>
                </div>
              )}
              <div className="sites-section-header">
                <h3>Contractors ({filteredSubCompanies.length})</h3>
                <div className="sites-header-actions">
                  <div className="search-container">
                    <input
                      type="text"
                      placeholder="Search contractor name..."
                      value={contractorSearchQuery}
                      onChange={(e) => setContractorSearchQuery(e.target.value)}
                      className="search-input"
                    />
                    <span className="search-icon">🔍</span>
                  </div>
                  <button
                    className="refresh-button"
                    onClick={() => loadSubCompanies(parentCompany.companyId || parentCompany.id)}
                    disabled={isLoadingSubCompanies}
                    title="Refresh list"
                  >
                    ↻ Refresh
                  </button>
                  <button
                    className="add-site-button"
                    onClick={() => {
                      setShowAddSubCompanyForm(!showAddSubCompanyForm);
                      setShowEditSubCompanyForm(false);
                      setSubCompanyToEdit(null);
                      setAddSubCompanyError('');
                    }}
                  >
                    {showAddSubCompanyForm ? 'Cancel' : '+ Add Contractor'}
                  </button>
                </div>
              </div>

              {showAddSubCompanyForm && (
                <div
                  className="company-modal-overlay"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Add new contractor"
                  onClick={handleSubCompanyCancel}
                >
                  <div className="company-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="form-card">
                      <div className="form-card-header company-modal-header">
                        <button
                          type="button"
                          className="company-modal-close"
                          onClick={handleSubCompanyCancel}
                          aria-label="Close"
                          title="Close"
                        >
                          ×
                        </button>
                        <div className="form-icon">👷</div>
                        <h2>Add New Contractor</h2>
                        <p className="form-subtitle">Create a sub-company under {parentCompany.companyName}</p>
                      </div>
                      {addSubCompanyError && (
                        <div className="error-message" role="alert" aria-live="polite">
                          <span className="message-icon" aria-hidden="true">!</span>
                          <span className="error-message-text">{addSubCompanyError}</span>
                          <button
                            type="button"
                            className="error-message-close"
                            onClick={() => setAddSubCompanyError('')}
                            aria-label="Close message"
                            title="Close"
                          >
                            ×
                          </button>
                        </div>
                      )}
                      <form onSubmit={handleSubCompanySubmit} className="company-form">
                        <div className="form-group">
                          <label htmlFor="subCompanyName">
                            Contractor Name <span className="required">*</span>
                            <span className="character-count">({subCompanyFormData.companyName.length}/500)</span>
                          </label>
                          <input
                            type="text"
                            id="subCompanyName"
                            name="companyName"
                            value={subCompanyFormData.companyName}
                            onChange={handleSubCompanyInputChange}
                            placeholder="Enter contractor name"
                            className={subCompanyFormErrors.companyName ? 'error' : ''}
                            disabled={isSubmittingSubCompany}
                            maxLength={500}
                          />
                          {subCompanyFormErrors.companyName && (
                            <span className="field-error">{subCompanyFormErrors.companyName}</span>
                          )}
                        </div>
                        <div className="form-group">
                          <label htmlFor="subCompanyMailId">
                            Contractor Email <span className="optional">(Optional)</span>
                          </label>
                          <input
                            type="email"
                            id="subCompanyMailId"
                            name="companyMailId"
                            value={subCompanyFormData.companyMailId}
                            onChange={handleSubCompanyInputChange}
                            placeholder="contact@example.com"
                            className={subCompanyFormErrors.companyMailId ? 'error' : ''}
                            disabled={isSubmittingSubCompany}
                          />
                          {subCompanyFormErrors.companyMailId && (
                            <span className="field-error">{subCompanyFormErrors.companyMailId}</span>
                          )}
                        </div>
                        <div className="form-actions">
                          <button
                            type="button"
                            className="cancel-button"
                            onClick={handleSubCompanyCancel}
                            disabled={isSubmittingSubCompany}
                          >
                            Cancel
                          </button>
                          <button type="submit" className="submit-button" disabled={isSubmittingSubCompany}>
                            {isSubmittingSubCompany ? (
                              <>
                                <span className="button-spinner"></span>
                                Creating...
                              </>
                            ) : (
                              <>
                                <span>✓</span>
                                Create Contractor
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}

              {showEditSubCompanyForm && subCompanyToEdit && (
                <div className="add-company-form-container edit-company-inline">
                  <div className="form-card">
                    <div className="form-card-header">
                      <div className="form-icon">✏️</div>
                      <h2>Edit Contractor</h2>
                      <p className="form-subtitle">Update contractor details</p>
                    </div>
                    <form onSubmit={handleUpdateSubCompany} className="company-form">
                      <div className="form-group">
                        <label htmlFor="editSubCompanyName">
                          Contractor Name <span className="required">*</span>
                          <span className="character-count">({editSubCompanyData.companyName.length}/500)</span>
                        </label>
                        <input
                          type="text"
                          id="editSubCompanyName"
                          name="companyName"
                          value={editSubCompanyData.companyName}
                          onChange={handleEditSubCompanyInputChange}
                          placeholder="Enter contractor name"
                          className={editSubCompanyErrors.companyName ? 'error' : ''}
                          disabled={isUpdatingSubCompany}
                          maxLength={500}
                        />
                        {editSubCompanyErrors.companyName && (
                          <span className="field-error">{editSubCompanyErrors.companyName}</span>
                        )}
                      </div>
                      <div className="form-group">
                        <label htmlFor="editSubCompanyMailId">
                          Contractor Email <span className="optional">(Optional)</span>
                        </label>
                        <input
                          type="email"
                          id="editSubCompanyMailId"
                          name="companyMailId"
                          value={editSubCompanyData.companyMailId}
                          onChange={handleEditSubCompanyInputChange}
                          placeholder="contact@example.com"
                          className={editSubCompanyErrors.companyMailId ? 'error' : ''}
                          disabled={isUpdatingSubCompany}
                        />
                        {editSubCompanyErrors.companyMailId && (
                          <span className="field-error">{editSubCompanyErrors.companyMailId}</span>
                        )}
                      </div>
                      <div className="form-actions">
                        <button
                          type="button"
                          className="cancel-button"
                          onClick={handleEditSubCompanyCancel}
                          disabled={isUpdatingSubCompany}
                        >
                          Cancel
                        </button>
                        <button type="submit" className="submit-button" disabled={isUpdatingSubCompany}>
                          {isUpdatingSubCompany ? (
                            <>
                              <span className="button-spinner"></span>
                              Updating...
                            </>
                          ) : (
                            <>
                              <span>✓</span>
                              Update Contractor
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {isLoadingSubCompanies ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading contractors...</p>
                </div>
              ) : filteredSubCompanies.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">👷</div>
                  <h3>{contractorSearchQuery ? 'No Contractors Found' : 'No Contractors Found'}</h3>
                  <p>
                    {contractorSearchQuery
                      ? `No contractors match "${contractorSearchQuery}".`
                      : 'Add your first contractor for this company.'}
                  </p>
                  {!showAddSubCompanyForm && !contractorSearchQuery && (
                    <button className="empty-state-button" onClick={() => setShowAddSubCompanyForm(true)}>
                      <span>+</span> Add Your First Contractor
                    </button>
                  )}
                </div>
              ) : (
                <div className="companies-list">
                  {filteredSubCompanies.map((subCompany) => {
                    const stateCode = states.find((s) => Number(s.stateId) === Number(subCompany.stateId))?.stateCode;
                    return (
                    <div key={subCompany.companyId || subCompany.id} className="company-card">
                      {stateCode && (
                        <span className="company-card-state-badge" title={stateCode}>{stateCode}</span>
                      )}
                      <div className="company-card-accent"></div>
                      <div className="company-card-content">
                        <div className="company-card-top">
                          <div className="company-icon-wrapper">
                            <svg className="company-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                              <polyline points="9 22 9 12 15 12 15 22"/>
                            </svg>
                          </div>
                          <div className="company-title-block">
                            <h3 className="company-name">{subCompany.companyName || 'Unnamed Company'}</h3>
                          </div>
                        </div>
                        <div className="company-card-body">
                          <div className="company-details-list">
                            {subCompany.companyMailId ? (
                              <div className="company-detail">
                                <svg className="detail-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                                <a href={`mailto:${subCompany.companyMailId}`} className="company-email">{subCompany.companyMailId}</a>
                              </div>
                            ) : (
                              <div className="company-detail company-detail-empty">No email</div>
                            )}
                            {subCompany.startDate ? (
                              <div className="company-detail">
                                <svg className="detail-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                <span className="detail-value">{formatDate(subCompany.startDate)}</span>
                              </div>
                            ) : (
                              <div className="company-detail company-detail-empty">No start date</div>
                            )}
                          </div>
                        </div>
                        <div className="company-card-actions">
                          <button
                            type="button"
                            className="company-card-btn view-sites-button"
                            onClick={() =>
                              handleViewSites(subCompany.companyId || subCompany.id, {
                                fromContractors: true,
                                parent: parentCompany,
                              })
                            }
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                            View Sites
                          </button>
                          <button
                            type="button"
                            className="company-card-btn edit-company-button"
                            onClick={() => handleEditSubCompanyClick(subCompany)}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            Edit
                          </button>
                          <button
                            type="button"
                            className="company-card-btn delete-company-button"
                            onClick={() => handleDeleteSubCompanyClick(subCompany)}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <>
          {/* Error Message (page-level) */}
          {error && (
            <div className="error-message" role="alert" aria-live="polite">
              <span className="message-icon" aria-hidden="true">!</span>
              <span className="error-message-text">{error}</span>
              <button
                type="button"
                className="error-message-close"
                onClick={() => setError('')}
                aria-label="Close message"
                title="Close"
              >
                ×
              </button>
            </div>
          )}

          {/* Add Company Form */}
          {showAddForm && (
            <div
              className="company-modal-overlay"
              role="dialog"
              aria-modal="true"
              aria-label="Add new company"
              onClick={() => {
                setShowAddForm(false);
                setAddCompanyError('');
              }}
            >
              <div className="company-modal" onClick={(e) => e.stopPropagation()}>
                <div className="form-card">
                  <div className="form-card-header company-modal-header">
                    <button
                      type="button"
                      className="company-modal-close"
                      onClick={() => {
                        setShowAddForm(false);
                        setAddCompanyError('');
                      }}
                      aria-label="Close"
                      title="Close"
                    >
                      ×
                    </button>
                    <div className="form-icon">🏢</div>
                    <h2>Add New Company</h2>
                    <p className="form-subtitle">Fill in the details to create a new company</p>
                  </div>
                  {addCompanyError && (
                    <div className="error-message" role="alert" aria-live="polite">
                      <span className="message-icon" aria-hidden="true">!</span>
                      <span className="error-message-text">{addCompanyError}</span>
                      <button
                        type="button"
                        className="error-message-close"
                        onClick={() => setAddCompanyError('')}
                        aria-label="Close message"
                        title="Close"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  <form onSubmit={handleSubmit} className="company-form">
                    <div className="form-group">
                      <label htmlFor="stateId">
                        State <span className="required">*</span>
                      </label>
                      <select
                        id="stateId"
                        name="stateId"
                        value={formData.stateId}
                        onChange={handleInputChange}
                        className={formErrors.stateId ? 'error' : ''}
                        disabled={isSubmitting}
                      >
                        <option value="">Select State</option>
                        {states.map((state) => (
                          <option key={state.stateId} value={state.stateId}>
                            {state.stateName}
                          </option>
                        ))}
                      </select>
                      {formErrors.stateId && (
                        <span className="field-error">{formErrors.stateId}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="companyName">
                        Company Name <span className="required">*</span>
                        <span className="character-count">({formData.companyName.length}/500)</span>
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        placeholder="Enter company name"
                        className={formErrors.companyName ? 'error' : ''}
                        disabled={isSubmitting}
                        maxLength={500}
                      />
                      {formErrors.companyName && (
                        <span className="field-error">{formErrors.companyName}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="companyMailId">
                        Company Email <span className="optional">(Optional)</span>
                      </label>
                      <input
                        type="email"
                        id="companyMailId"
                        name="companyMailId"
                        value={formData.companyMailId}
                        onChange={handleInputChange}
                        placeholder="contact@example.com"
                        className={formErrors.companyMailId ? 'error' : ''}
                        disabled={isSubmitting}
                      />
                      {formErrors.companyMailId && (
                        <span className="field-error">{formErrors.companyMailId}</span>
                      )}
                    </div>

                    <div className="form-actions">
                      <button
                        type="button"
                        className="cancel-button"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="submit-button"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="button-spinner"></span>
                            Creating...
                          </>
                        ) : (
                          <>
                            <span>✓</span>
                            Create Company
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Companies List */}
          <div className="companies-section" ref={companiesSectionRef}>
            <div className="section-header">
              <h2>Companies ({filteredCompanies.length})</h2>
              <div className="section-header-actions">
                <div className="filter-state-container">
                  <select
                    value={filterStateId}
                    onChange={(e) => setFilterStateId(e.target.value)}
                    className="filter-state-select"
                    aria-label="Filter by state"
                  >
                    <option value="">All states</option>
                    {states.map((state) => (
                      <option key={state.stateId} value={state.stateId}>
                        {state.stateName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="search-container">
                  <input
                    type="text"
                    placeholder="Search company name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                  <span className="search-icon">🔍</span>
                </div>
                <button 
                  className="refresh-button"
                  onClick={loadCompanies}
                  disabled={isLoading}
                  title="Refresh list"
                >
                  ↻ Refresh
                </button>
                <button 
                  className="add-company-button"
                  onClick={() => {
                    setShowAddForm(!showAddForm);
                    setAddCompanyError('');
                    setShowEditCompanyForm(false);
                    setCompanyToEdit(null);
                  }}
                >
                  {showAddForm ? 'Cancel' : '+ Add Company'}
                </button>
              </div>
            </div>

            {/* Inline Edit Company Form */}
            {showEditCompanyForm && companyToEdit && (
              <div className="add-company-form-container edit-company-inline">
                <div className="form-card">
                  <div className="form-card-header">
                    <div className="form-icon">✏️</div>
                    <h2>Edit Company</h2>
                    <p className="form-subtitle">Update company details</p>
                  </div>
                  <form onSubmit={handleUpdateCompany} className="company-form">
                    <div className="form-group">
                      <label htmlFor="editStateId">
                        State <span className="required">*</span>
                      </label>
                      <select
                        id="editStateId"
                        name="stateId"
                        value={editCompanyData.stateId}
                        onChange={handleEditCompanyInputChange}
                        className={editCompanyErrors.stateId ? 'error' : ''}
                        disabled={isUpdatingCompany}
                      >
                        <option value="">Select State</option>
                        {states.map((state) => (
                          <option key={state.stateId} value={state.stateId}>
                            {state.stateName}
                          </option>
                        ))}
                      </select>
                      {editCompanyErrors.stateId && (
                        <span className="field-error">{editCompanyErrors.stateId}</span>
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor="editCompanyName">
                        Company Name <span className="required">*</span>
                        <span className="character-count">({editCompanyData.companyName.length}/500)</span>
                      </label>
                      <input
                        type="text"
                        id="editCompanyName"
                        name="companyName"
                        value={editCompanyData.companyName}
                        onChange={handleEditCompanyInputChange}
                        placeholder="Enter company name"
                        className={editCompanyErrors.companyName ? 'error' : ''}
                        disabled={isUpdatingCompany}
                        maxLength={500}
                      />
                      {editCompanyErrors.companyName && (
                        <span className="field-error">{editCompanyErrors.companyName}</span>
                      )}
                    </div>
                    <div className="form-group">
                      <label htmlFor="editCompanyMailId">
                        Company Email <span className="optional">(Optional)</span>
                      </label>
                      <input
                        type="email"
                        id="editCompanyMailId"
                        name="companyMailId"
                        value={editCompanyData.companyMailId}
                        onChange={handleEditCompanyInputChange}
                        placeholder="contact@example.com"
                        className={editCompanyErrors.companyMailId ? 'error' : ''}
                        disabled={isUpdatingCompany}
                      />
                      {editCompanyErrors.companyMailId && (
                        <span className="field-error">{editCompanyErrors.companyMailId}</span>
                      )}
                    </div>
                    <div className="form-actions">
                      <button
                        type="button"
                        className="cancel-button"
                        onClick={handleEditCompanyCancel}
                        disabled={isUpdatingCompany}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="submit-button"
                        disabled={isUpdatingCompany}
                      >
                        {isUpdatingCompany ? (
                          <>
                            <span className="button-spinner"></span>
                            Updating...
                          </>
                        ) : (
                          <>
                            <span>✓</span>
                            Update Company
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {isLoading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading companies...</p>
              </div>
            ) : filteredCompanies.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🏢</div>
                <h3>{searchQuery || filterStateId ? 'No Companies Found' : 'No Companies Found'}</h3>
                <p>{searchQuery || filterStateId ? `No companies match the selected filters. Try a different search term or state.` : 'Get started by adding your first company to begin managing your organization.'}</p>
                {!showAddForm && !searchQuery && !filterStateId && (
                  <button 
                    className="empty-state-button"
                    onClick={() => setShowAddForm(true)}
                  >
                    <span>+</span> Add Your First Company
                  </button>
                )}
              </div>
            ) : (
              <div className="companies-list">
                {filteredCompanies.map((company) => {
                  const stateCode = states.find((s) => Number(s.stateId) === Number(company.stateId))?.stateCode;
                  return (
                  <div key={company.companyId || company.id} className="company-card">
                    {stateCode && (
                      <span className="company-card-state-badge" title={stateCode}>{stateCode}</span>
                    )}
                    <div className="company-card-accent"></div>
                    <div className="company-card-content">
                      <div className="company-card-top">
                        <div className="company-icon-wrapper">
                          <svg className="company-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                            <polyline points="9 22 9 12 15 12 15 22"/>
                          </svg>
                        </div>
                        <div className="company-title-block">
                          <h3 className="company-name">{company.companyName || 'Unnamed Company'}</h3>
                        </div>
                      </div>
                      <div className="company-card-body">
                        <div className="company-details-list">
                          {company.companyMailId ? (
                            <div className="company-detail">
                              <svg className="detail-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                              <a href={`mailto:${company.companyMailId}`} className="company-email">{company.companyMailId}</a>
                            </div>
                          ) : (
                            <div className="company-detail company-detail-empty">No email</div>
                          )}
                          {company.startDate ? (
                            <div className="company-detail">
                              <svg className="detail-icon-svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                              <span className="detail-value">{formatDate(company.startDate)}</span>
                            </div>
                          ):(
                            <div className="company-detail company-detail-empty">No start date</div>
                          )}
                        </div>
                      </div>
                      <div className="company-card-actions">
                        <button type="button" className="company-card-btn view-sites-button" onClick={() => handleViewSites(company.companyId || company.id)}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                          View Sites
                        </button>
                        <button type="button" className="company-card-btn view-contractors-button" onClick={() => handleViewContractors(company.companyId || company.id)}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                          View Contractors
                        </button>
                        <button type="button" className="company-card-btn edit-company-button" onClick={() => handleEditCompanyClick(company)}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          Edit
                        </button>
                        <button type="button" className="company-card-btn delete-company-button" onClick={() => handleDeleteClick(company)}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && companyToDelete && (
        <div className="delete-confirm-overlay" onClick={handleDeleteCancel}>
          <div className="delete-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-icon">⚠️</div>
            <h3>Confirm Deletion</h3>
            <p>
              Are you sure you want to remove <strong>{companyToDelete.companyName || (deleteContext === 'subCompany' ? 'this contractor' : 'this company')}</strong>?
              This action cannot be undone.
            </p>
            <div className="delete-confirm-actions">
              <button
                className="delete-cancel-button"
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="delete-confirm-button"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <span className="button-spinner"></span>
                    Removing...
                  </>
                ) : (
                  deleteContext === 'subCompany' ? 'Remove Contractor' : 'Remove Company'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Site Confirmation Dialog */}
      {showDeleteSiteConfirm && siteToDelete && (
        <div className="delete-confirm-overlay" onClick={handleDeleteSiteCancel}>
          <div className="delete-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="delete-confirm-icon">⚠️</div>
            <h3>Confirm Deletion</h3>
            <p>
              Are you sure you want to remove <strong>{siteToDelete.siteName || 'this site'}</strong>?
              This action cannot be undone.
            </p>
            <div className="delete-confirm-actions">
              <button
                className="delete-cancel-button"
                onClick={handleDeleteSiteCancel}
                disabled={isDeletingSite}
              >
                Cancel
              </button>
              <button
                className="delete-confirm-button"
                onClick={handleDeleteSiteConfirm}
                disabled={isDeletingSite}
              >
                {isDeletingSite ? (
                  <>
                    <span className="button-spinner"></span>
                    Removing...
                  </>
                ) : (
                  'Remove Site'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanySites;

